import { Col, Radio, Row, Spin, Tag } from "antd";
import { useState } from "react";
import { renderErrorMessage } from "../../../../../components/messages/errorMessage";
import { NewCustomEquipment } from "../../../../../components/modal/addCustomEquipment";
import MotorAtlasLink from "../../../../../components/motorAtlasLink";
import TaxCaseStore from "../../../../../contexts/taxCaseStore";
import TaxFlowStore from "../../../../../contexts/taxFlowStore";
import _t from "../../../../../lang/translate";
import { EquipmentCatalog, NewPriceManager, ServerEquipment } from "../../../../../types/newPriceTypes";
import { Strict } from "../../../../../types/utilTypes";
import openAsyncModal from "../../../motorAtlasCatalogMapper/openAsyncModal";
import EquipmentManager from "../../newPrice/equipmentManager";
import { calculateEquipmentPrice } from "../../newPrice/utils/calcNewPrice";
import { applyPackagesToEquipment, mapNewPriceCalculationComponents } from "../../newPrice/utils/mappers";
import { initializeAdvertEquipment } from "../../taxFlow/util";
import { inferEnrichment, requestEnrichment, updateAdvertMotorAtlasId } from "../service";
import { AdvertEquipmentSource, EnrichmentCompletedPayload, TaxAdvert } from "../types";
import { EnrichmentData } from "../advertDetails";
import { isValidNum } from "../../../../../utilities/typeGuard";
import { isValidVin } from "../../../../../utilities/stringExtraFunctions";


type AdvertEquipmentProps = {
  advert: TaxAdvert
  setNewPriceManager: React.Dispatch<React.SetStateAction<NewPriceManager | undefined>>
  newPriceManager: NewPriceManager | undefined
  equipmentSource: AdvertEquipmentSource
  setEquipmentSource: React.Dispatch<React.SetStateAction<AdvertEquipmentSource>>
  setAdvertEcode: React.Dispatch<React.SetStateAction<string | null | undefined>>
  advertEcode: string | null | undefined,
  enrichmentData: EnrichmentData;
  setEnrichmentData: React.Dispatch<React.SetStateAction<EnrichmentData>>;
  enrichmentQuality: "LOW" | "HIGH";
  vinOverride: string | undefined | null;
}
export default function AdvertEquipment({
  advert, newPriceManager, setNewPriceManager, equipmentSource, setEquipmentSource, advertEcode, setAdvertEcode,
  setEnrichmentData, enrichmentData, enrichmentQuality, vinOverride
}: AdvertEquipmentProps) {
  const { taxCase } = TaxCaseStore.useContainer()
  const { taxFlow } = TaxFlowStore.useContainer()
  const [loadingAdvertSourceChange, setLoadingAdvertSourceChange] = useState(false)
  const equipment = newPriceManager?.data.equipment

  const vin = advert.vin ?? vinOverride

  async function openModelCatalog() {
    const data = await openAsyncModal({
      views: {
        hideSourceVehicle: false,
      },
      selectedMotorAtlasId: advert.config?.enrichment?.vinData?.eCode,
      // TODO: Fix this
      inputVehicle: {
        firstRegDate: advert.firstRegDate,
        vin: vin,
        link: null,
        mileage: advert.mileage,
        motorAtlasId: advert.config?.enrichment?.vinData?.eCode,
        typeId: advert.type === "Varebil" ? 2 : advert.type === "Motorcykel" ? 3 : 1
      }
    })

    if (data?.selectedMatch.eCode) {
      await updateAdvertMotorAtlasId({
        adId: String(advert.id),
        motorAtlasId: data.selectedMatch.eCode
      })
      setAdvertEcode(data.selectedMatch.eCode)
      return data.selectedMatch.eCode;
    }
  }
  async function handleSourceChange(source: string) {
    try {

      setLoadingAdvertSourceChange(true)
      if (source === 'advert') {
        await retryAdvertEnrichment()
      } else {
        await retryBasicEnrichment()
      }
      setEquipmentSource(source as any)
    }
    catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoadingAdvertSourceChange(false)
    }
  }

  async function retryAdvertEnrichment() {
    let eCode = advertEcode;
    if (!advertEcode) {
      eCode = await openModelCatalog()
    }
    const start = new Date().toISOString();
    let data = null;
    if (enrichmentQuality === 'HIGH' && isValidVin(vin)) {
      const { data: enrichmentData } = await requestEnrichment({
        payloads: [{
          advertId: String(advert.id),
          identifierECode: eCode ?? "",
          priceDate: advert?.firstRegDate,
          sync: true,
          forceRefresh: true
        }]
      })
      data = enrichmentData
    } else {
      const { data: enrichmentData } = await inferEnrichment({
        payloads: [{
          advertId: String(advert.id),
          identifierECode: eCode ?? "",
          priceDate: advert?.firstRegDate,
          sync: true,
          forceRefresh: true
        }]
      })
      data = enrichmentData
    }
    const enrichment = data?.data?.[advert.id];

    const vinEquipmentCodes = new Set(enrichment.vinData?.equipment);
    const vinEquipment = enrichment.potentialEquipment?.filter(eq => vinEquipmentCodes.has(eq.code)) ?? [];
    const potentialEquipment = enrichment.potentialEquipment?.filter(eq => !vinEquipmentCodes.has(eq.code)) ?? [];
    const newPriceData = mapNewPriceCalculationComponents({
      newPrices: enrichment?.newPrices?.map(mapEnrichmentNewPrices) ?? [],
      potentialEquipment: potentialEquipment.map(mapEnrichmentPotentialEquipment),
      vinEquipment: vinEquipment.map(mapEnrichmentPotentialEquipment),
    }, null, {
      emissionAmount: advert.emissionAmount
    })

    const mountedCodes = new Set(enrichment.mountedEquipment)
    const equipment = newPriceData.equipment.map((eq) => {
      return {
        ...eq,
        mounted: eq.code ? mountedCodes.has(eq.code) : false
      }
    })

    setNewPriceManager({
      ...newPriceManager,
      data: {
        ...newPriceData,
        equipment,
      },
      values: {
        newPrice: null,
        equipmentPrice: calculateEquipmentPrice(newPriceData.equipment)
      },
    })

    const now = new Date().toISOString();
    setEnrichmentData({
      enrichment: enrichment,
      enrichmentMeta: {
        status: enrichment.completionStatus === "success" ? "ready" : "failed",
        completedAt: now,
        attemptCount: (enrichmentData.enrichmentMeta?.attemptCount ?? 0) + 1,
        lastAttemptAt: now,
        startedAt: start
      }
    })
  }

  async function retryBasicEnrichment() {
    const start = new Date().toISOString();
    let data = null;
    if (enrichmentQuality === 'HIGH' && isValidVin(vin)) {
      const { data: enrichmentData } = await requestEnrichment({
        payloads: [{
          advertId: String(advert.id),
          identifierECode: taxCase.vehicle?.motorAtlasId ?? "",
          priceDate: taxCase.vehicle?.firstRegDate,
          sync: true,
          forceRefresh: true
        }]
      })
      data = enrichmentData
    } else {
      const { data: enrichmentData } = await inferEnrichment({
        payloads: [{
          advertId: String(advert.id),
          identifierECode: taxCase.vehicle?.motorAtlasId ?? "",
          priceDate: taxCase.vehicle?.firstRegDate,
          sync: true,
          forceRefresh: true
        }]
      })
      data = enrichmentData
    }
    const enrichment = data?.data?.[advert.id];
    if (!advertEcode && enrichment.vinData?.eCode) {
      setAdvertEcode(enrichment.vinData?.eCode)
    }

    const newPriceManager = initializeAdvertEquipment(taxFlow, enrichment)
    setNewPriceManager(newPriceManager)
    const now = new Date().toISOString();
    setEnrichmentData({
      enrichment: enrichment,
      enrichmentMeta: {
        status: enrichment.completionStatus === "success" ? "ready" : "failed",
        completedAt: now,
        attemptCount: (enrichmentData.enrichmentMeta?.attemptCount ?? 0) + 1,
        lastAttemptAt: now,
        startedAt: start,
      }
    })
  }

  function setMountedEquipment(code: string, checked: boolean) {
    setNewPriceManager((prev) => {
      let newEquipment = prev?.data.equipment.map(eq => {
        if (eq.code !== code) return eq;
        return {
          ...eq,
          mounted: checked
        }
      }) ?? []
      newEquipment = applyPackagesToEquipment(newEquipment)

      return {
        ...prev,
        data: {
          ...prev?.data,
          newPrices: prev?.data.newPrices ?? [],
          selectedNewPrice: prev?.data.selectedNewPrice ?? null,
          equipment: newEquipment
        },
        values: {
          ...prev?.values,
          newPrice: prev?.values.newPrice ?? null,
          equipmentPrice: calculateEquipmentPrice(newEquipment ?? [])
        }
      }
    })
  }

  function addCustomEquipment(customEquipment: NewCustomEquipment) {
    const existingIndex = equipment?.findIndex(item => item.code?.toLowerCase() === customEquipment.code?.toLowerCase()) ?? -1

    if (existingIndex >= 0 && !(equipment ?? [])[existingIndex].isCustom) {
      // Check if the existing equipment is custom, then override, otherwise throw error
      throw new Error(_t("msg.equipment_code_already_exists"))
    }
    setNewPriceManager((prev) => {
      const equipment = [...prev?.data?.equipment ?? []];
      const newEq: EquipmentCatalog = {
        code: customEquipment.code,
        name: customEquipment.name,
        category: customEquipment.category,
        amount: customEquipment.price,
        source: "CUSTOM",
        sourceId: null,
        isWithTax: true,
        mounted: true,

        isCustom: true,

        isIncludedInMountedPackage: false,
        isVinEquipment: false,
        packageContent: [],
        includedInNewPrice: false,
        includedInPackage: [],
        isForceMounted: false,
        isForceMountedByNewPrice: false,
        isForceMountedByPackage: false,
      }

      if (existingIndex >= 0) {
        equipment[existingIndex] = newEq
      } else {
        equipment.push(newEq)
      }

      const equipmentPrice = calculateEquipmentPrice(equipment)

      return {
        ...prev,
        data: {
          ...prev?.data,
          newPrices: prev?.data.newPrices ?? [],
          selectedNewPrice: prev?.data.selectedNewPrice ?? null,
          equipment: equipment ?? []
        },
        values: {
          ...prev?.values,
          newPrice: prev?.values.newPrice ?? null,
          equipmentPrice: equipmentPrice
        }
      }
    })
  }
  async function handleFetch() {
    try {
      setLoadingAdvertSourceChange(true)
      if (equipmentSource === 'taxCase') {
        await retryBasicEnrichment()
      } else {
        await retryAdvertEnrichment()
      }
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoadingAdvertSourceChange(false)
    }
  }



  return (
    <div>
      <div className="border-radius border-light bg-light mb-05 p-1">
        <p> {_t("choose", "source")} </p>
        <Radio.Group style={{ display: "flex" }}
          value={equipmentSource}
          onChange={(v) => handleSourceChange(v.target.value)}
          className="advert-equipment-source-radio"
        >
          <Radio className={`border-light  border-radius ${equipmentSource === 'taxCase' ? 'border-primary bg-white' : ''}`}
            value={"taxCase"}
            style={{
              flex: "1 1 50%",
              padding: 12,
            }}
          >
            <Spin spinning={loadingAdvertSourceChange}>
              <Row className="w-100" justify="space-between" align="middle">
                <Col>
                  Vurderingskøretøj {' '}
                  <span className="text-tiny text-primary">({_t("recommended")})</span>
                </Col>
                <Col>
                  <MotorAtlasLink motorAtlasId={taxCase.vehicle?.motorAtlasId}
                    firstRegDate={taxCase.vehicle?.firstRegDate}
                    motorAtlasTab="udstyr"
                    isVan={taxCase.vehicle?.typeId === 2}
                  />
                </Col>
              </Row>
              <div className="mt-05">
                <p className="mb-0 text-smallest muted">Udstyrskatalog baseret på vurderingskøretøjets model:</p>
                <p className="mb-0 text-smallest text-semibold">{taxCase.vehicle?.brand} {taxCase.vehicle?.model}</p>
              </div>
            </Spin>
          </Radio>

          <Radio className={`border-light  border-radius ${equipmentSource === 'advert' ? 'border-primary bg-white' : ''}`}
            disabled={advert.config?.isCustom}
            value={"advert"}
            style={{
              flex: "1 1 50%",
              padding: 12,
            }}
          >
            <Spin spinning={loadingAdvertSourceChange}>
              <Row className="w-100" justify="space-between" align="middle">
                <Col>
                  Annoncekøretøj (VIN)
                </Col>
                <Col>
                  <MotorAtlasLink motorAtlasId={advertEcode}
                    firstRegDate={advert.firstRegDate}
                    motorAtlasTab="udstyr"
                    isVan={advert.type === "Varebil"}
                  />
                </Col>
              </Row>
              <div className="mt-05">
                <p className="mb-0 text-smallest muted">Udstyr baseret på annoncekøretøjets e-kode fundet via stelnummer:</p>
                <p className="mb-0 text-smallest text-semibold">{advert.manufacturer} {advert.name} - {vin ?? "INGEN STEL.NR."}</p>
              </div>
              {advert.config?.isCustom && (
                <div className="mt-05">
                  <p className="mb-0 text-smallest text-danger font-bold">På manuelt oprettede annoncer kan du ikke tilføje en ecode.</p>
                </div>
              )}
            </Spin>
          </Radio>
        </Radio.Group>
      </div>
      <EquipmentManager
        equipment={equipment ?? []}
        setMounted={setMountedEquipment}
        addCustomEquipment={addCustomEquipment}
        reloadData={handleFetch}
        vehicle={{
          firstRegDate: taxCase.vehicle?.firstRegDate ?? null,
          motorAtlasId: taxCase.vehicle?.motorAtlasId ?? null,
          vin: taxCase.vehicle?.vin ?? null,
          type: taxCase.vehicle?.typeId
        }}
      />

    </div>
  )
}

type PotentialEquipment = Strict<EnrichmentCompletedPayload>['potentialEquipment'][number];
export function mapEnrichmentPotentialEquipment(
  equipment: PotentialEquipment,
  _i: number,
  arr: PotentialEquipment[]
): ServerEquipment {
  return mapEnrichmentPotentialEquipmentSafe(equipment, arr, new Set());
}

function mapEnrichmentPotentialEquipmentSafe(
  equipment: PotentialEquipment,
  allEquipment: PotentialEquipment[],
  visited: Set<string>
): ServerEquipment {
  const key = equipment.code || equipment.id;

  if (key && visited.has(key)) {
    return {
      author: equipment.author,
      code: equipment.code,
      id: equipment.id,
      name: equipment.description,
      savedAt: equipment.savedAt,
      typeCategory: equipment?.category?.id,
      prices: equipment.prices ?? [],
      subEquipments: [],
      source: equipment?.prices?.[0]?.source?.name ?? null,
      currencyCode: null,
      equipmentCategory: null,
      price: equipment?.prices?.[0]?.amount ?? null,
      priceIsKnown: isValidNum(equipment?.prices?.[0]),
      sourceId: equipment?.prices?.[0]?.source?.id ?? null,
    };
  }

  const nextVisited = new Set(visited);
  if (key) nextVisited.add(key);

  const equipmentIncluded = new Set(equipment.equipmentIncluded);
  const price = equipment?.prices?.[0];
  const source = price?.source;
  const amount = price?.amount ?? null;

  return {
    author: equipment.author,
    code: equipment.code,
    id: equipment.id,
    name: equipment.description,
    savedAt: equipment.savedAt,
    typeCategory: equipment?.category?.id,
    prices: equipment.prices ?? [],
    subEquipments: allEquipment
      .filter(item => item.code !== equipment.code && equipmentIncluded.has(item.code))
      .map(item => mapEnrichmentPotentialEquipmentSafe(item, allEquipment, nextVisited)),
    source: source?.name ?? null,
    currencyCode: null,
    equipmentCategory: null,
    price: amount,
    priceIsKnown: isValidNum(price),
    sourceId: source?.id ?? null,
  };
}
export function mapEnrichmentNewPrices(item: Strict<EnrichmentCompletedPayload>['newPrices'][number]) {
  return {
    equipmentIncludedInPrice: item.equipmentIncludedInPrice,
    equipmentForceMounted: item.equipmentForceMounted,
    id: item.id,
    source: item?.source?.name ?? null,
    sourceId: item?.source?.id ?? null,
    amount: item.amount ?? 0,
    author: item.author,
    currencyCode: item.currency,
    dateGroup: item.dateGroup,
    documents: item.documents,
    note: item.note,
    prioritized: item.prioritized,
    savedAt: item.savedAt ?? "",
  }
}
